<template>
  <div class="dashboard-card camembert-card">
    <h3 class="camembert-title">Répartition des actions</h3>
    <div class="chart-bg">
      <div class="chart-container" ref="pieChart"></div>
      <div v-if="isAllZero && !error" class="empty-msg">Aucune donnée à afficher</div>
      <div v-if="error" class="error-msg">{{ error }}</div>
    </div>
    <div class="action-legend">
      <div class="legend-item"><span class="legend-color bleu"></span> En cours ({{ percentage.en_cours }}%)</div>
      <div class="legend-item"><span class="legend-color gris"></span> Non commencée ({{ percentage.non_commencee }}%)</div>
      <div class="legend-item"><span class="legend-color vert"></span> Terminée ({{ percentage.terminee }}%)</div>
    </div>
  </div>
</template>

<script>
import Chartist from 'chartist';
import 'chartist/dist/chartist.min.css';
import axios from 'axios';
import { safeErrorMessage } from '@/utils/errorMessages';
import { isAuthenticatedSession } from '@/utils/sessionAuth';

const RACI_API = '/api/raci/';

export default {
  data() {
    return {
      actionStats: {
        en_cours: 0,
        non_commencee: 0,
        terminee: 0
      },
      error: null,
      chart: null
    };
  },
  computed: {
    isAllZero() {
      const s = this.actionStats;
      return s.en_cours === 0 && s.non_commencee === 0 && s.terminee === 0;
    },
    total() {
      const s = this.actionStats;
      return s.en_cours + s.non_commencee + s.terminee;
    },
    percentage() {
      if (this.total === 0) {
        return {
          en_cours: 0,
          non_commencee: 0,
          terminee: 0
        };
      }
      return {
        en_cours: Math.round((this.actionStats.en_cours / this.total) * 100),
        non_commencee: Math.round((this.actionStats.non_commencee / this.total) * 100),
        terminee: Math.round((this.actionStats.terminee / this.total) * 100)
      };
    }
  },
  async mounted() {
    await this.fetchStats();
    await this.$nextTick();
    this.renderChart();
  },
  methods: {
    async fetchStats() {
      if (!isAuthenticatedSession()) {
        console.error("Token non trouvé, veuillez vous reconnecter.");
        this.error = "Token non trouvé, veuillez vous reconnecter.";
        return;
      }

      try {
        const response = await axios.get(RACI_API, {
          withCredentials: true
        });

        const g = response.data?.statistiques_globales;
        if (g) {
          this.actionStats = {
            en_cours: g.recommandations_en_cours || 0,
            non_commencee: g.recommandations_non_commencee || 0,
            terminee: g.recommandations_terminees || 0
          };
        }
      } catch (error) {
        this.error = safeErrorMessage(error, "Une erreur est survenue lors du chargement des statistiques.");
      }
    },
    renderChart() {
      if (this.isAllZero) return;

      const total = this.total;
      const percentages = {
        en_cours: (this.actionStats.en_cours / total) * 100,
        non_commencee: (this.actionStats.non_commencee / total) * 100,
        terminee: (this.actionStats.terminee / total) * 100
      };

      const data = {
        series: [
          percentages.en_cours,
          percentages.non_commencee,
          percentages.terminee
        ]
      };

      const options = {
        donut: true,
        donutWidth: 50,
        startAngle: 0,
        showLabel: false, // Changé de true à false pour enlever les pourcentages
        chartPadding: 30,
        classNames: {
          chartPie: 'ct-chart-pie camembert-anim',
        }
      };

      if (this.chart) {
        this.chart.detach();
      }

      this.chart = new Chartist.Pie(this.$refs.pieChart, data, options);
      this.chart.on('draw', function (ctx) {
        if (ctx.type === 'slice') {
          const colors = ['#1a2238', '#9ca3af', '#2d8659'];
          ctx.element.attr({
            style: `fill: ${colors[ctx.index]}; transition: fill 0.4s;`
          });
        }
      });
    },
    updateChart() {
      if (this.chart) {
        this.chart.detach();
      }
      this.renderChart();
    }
  },
  beforeUnmount() {
    if (this.chart) {
      this.chart.detach();
    }
  }
};
</script>
<style scoped>
/* Suppression du fond jaune et autres ajustements */
.dashboard-card {
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid rgba(212, 175, 55, 0.2);
  border-radius: 12px;
  box-shadow: var(--shadow-md, 0 4px 14px rgba(10, 26, 47, 0.08));
  padding: 20px;
  backdrop-filter: blur(10px);
}

.chart-bg {
  background: transparent; /* Fond transparent pour la zone du graphique */
  position: relative;
}

/* Le reste des styles reste inchangé */
.camembert-title {
  font-size: 1.5rem;
  font-weight: var(--font-weight-semibold);
  font-family: var(--font-display), var(--font-primary);
  color: rgba(10, 26, 47, 0.92);
  margin-bottom: 1.2rem;
  text-align: center;
  letter-spacing: 0.02em;
}

.chart-container {
  min-height: 100%;
  width: 100%;
  transition: opacity 0.5s;
}

.action-legend {
  margin-top: 10px;
  display: flex;
  gap: 18px;
  flex-wrap: wrap;
  justify-content: center;
}

.legend-item {
  display: flex;
  align-items: center;
  font-size: 15px;
  cursor: pointer;
  transition: color 0.2s;
}

.legend-item:hover {
  color: rgba(10, 26, 47, 0.95);
  font-weight: 600;
}

.legend-color {
  width: 16px;
  height: 16px;
  border-radius: 3px;
  margin-right: 7px;
  display: inline-block;
  box-shadow: 0 1px 4px rgba(44, 62, 80, 0.10);
}

.bleu {
  background: #1a2238;
}

.gris {
  background: #9ca3af;
}

.vert {
  background: #2d8659;
}

.empty-msg {
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  text-align: center;
  color: var(--bank-gray-500);
  font-size: 1.1rem;
  font-style: italic;
  letter-spacing: 0.2px;
}

.error-msg {
  color: var(--bank-danger);
  text-align: center;
  margin-top: 10px;
}

@media (max-width: 600px) {
  .camembert-card {
    padding: 1.2rem 0.3rem 1rem 0.3rem;
    max-width: 100vw;
  }

  .chart-bg {
    min-height: 220px;
  }

  .chart-container {
    min-height: 120px;
  }
}
</style>
